/**
 * Treehouse tAsset periphery — the two exit modules a tAsset row reads.
 *
 * The tAsset share token itself is a plain ERC-4626 (`Erc4626ReadAbi`
 * covers it), but its `asset()` is an *internal accounting unit*
 * (`IAU_wstETH`, `IAU_sAVAX`, …) minted 1:1 against the real underlying —
 * so the underlying comes from the registry, never from `asset()`.
 * Everything that decides an exit lives on separate contracts.
 */

/** `TreehouseFastlane` — the instant (fee-paying) exit.
 *
 *  `getRedeemableAmount()` is the vault's underlying balance minus the
 *  underlying already earmarked by pending queued redemptions, i.e. exactly
 *  what a `redeemAndFinalize` can pay out this block. Denominated in the
 *  UNDERLYING (wstETH / sAVAX / kHYPE), not in shares. */
export const TreehouseFastlaneAbi = [
  {
    name: 'getRedeemableAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    // Delegates to a swappable fee contract, so it is a live read even
    // though the Fastlane itself is behind the timelock.
    name: 'redemptionFee',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'minRedeemInUnderlying',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint96' }],
  },
] as const

/** `TreehouseRedemptionV2` — the queued (cheap) exit.
 *
 *  `waitingPeriod()` is read LIVE rather than pinned in the registry: it is
 *  an owner dial behind the 5-day timelock and it already differs per chain
 *  (7 d Ethereum / 17 d Avalanche / 10 d HyperEVM) against docs that
 *  describe one 7-day wait everywhere. Same reasoning as the savings
 *  `erc4626-cooldown` reader — a pinned duration is the number that goes
 *  stale on the row. */
export const TreehouseRedemptionAbi = [
  {
    name: 'waitingPeriod',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint32' }],
  },
  {
    // The QUEUED leg charges too (5 bps). That contradicts the convention
    // `SavingsVault.withdrawFeeBps` documents — "the queued leg pays at par"
    // — which is exactly why the route list carries a fee PER LEG instead of
    // one field plus an assumption.
    name: 'redemptionFee',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint32' }],
  },
  {
    // The gate that decides whether the cheap leg exists for a given holder:
    // 50 wstETH / 4,000 sAVAX / 1,000 kHYPE, checked against
    // `previewRedeem(shares)`, i.e. in UNDERLYING units.
    name: 'minRedeemInUnderlying',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint96' }],
  },
] as const
